/* @flow */

import head from 'lodash/head';
import flatten from 'lodash/flatten';
import scrollIntoView from 'scroll-into-view-if-needed';
import { createSelector } from 'reselect';
import { LIMPAR_PEDIDO, type LimparPedidoAction } from './pedido';
import converterProdutoParaItem from '../../produtos/converter-produto-para-item';
import { precoTotalProduto } from '../../produtos/calculos';
import {
    buscarDados,
    getProdutoPorId,
    getGruposComplementos,
} from './grid-produtos';
import {
    getItemPorId,
    adicionarItem,
    editarItem,
    getItens,
} from './itens';
import { SELECAO_PRECO_MAIOR } from '../../complementos/constants';
import sum from '../../utils/sum';
import { type GetState, type State } from '../../preact-app/types';
import {
    type ProdutoPedidoCompleto,
    type ComplementoItem,
    type GrupoComplementoSelecao,
} from '../../produtos/types';
import {
    type GrupoComplemento,
    type Complemento,
} from '../../complementos/types';
import { type StateDetalhesProduto, type AdicionarComplemento } from './types';
import sleep from '../../utils/sleep';
import { navegarProximoElemento, navegarElementoAnterior } from '../../utils/navegar-entre-elementos';

type SetProdutoAction = {
    type: 'balcao/detalhes-produto/SET_PRODUTO',
    produto: ProdutoPedidoCompleto,
    grupos: GrupoComplemento[],
};

type SetIdAction = {
    type: 'balcao/detalhes-produto/SET_ID',
    id: number,
}

type AdicionarComplementoAction = {
    type: 'balcao/detalhes-produto/ADICIONAR_COMPLEMENTO',
    quantidade: number,
    complementosId: number,
    indice: number,
    grupo: GrupoComplemento,
};

type AlterarQuantidadeAction = {
    type: 'balcao/detalhes-produto/ALTERAR_QUANTIDADE',
    quantidade: number,
};

type AlterarObservacaoAction = {
    type: 'balcao/detalhes-produto/ALTERAR_OBSERVACAO',
    observacao: string,
};

type ResetAction = {
    type: 'balcao/detalhes-produto/RESET',
};

type Action =
    | AdicionarComplementoAction
    | SetProdutoAction
    | SetIdAction
    | AlterarObservacaoAction
    | ResetAction
    | LimparPedidoAction
    | AlterarQuantidadeAction;

// Action types

const SET_PRODUTO = 'balcao/detalhes-produto/SET_PRODUTO';
const SET_ID = 'balcao/detalhes-produto/SET_ID';
const ADICIONAR_COMPLEMENTO = 'balcao/detalhes-produto/ADICIONAR_COMPLEMENTO';
const ALTERAR_QUANTIDADE = 'balcao/detalhes-produto/ALTERAR_QUANTIDADE';
const ALTERAR_OBSERVACAO = 'balcao/detalhes-produto/ALTERAR_OBSERVACAO';
const RESET = 'balcao/detalhes-produto/RESET';

// Actions creators

const setProduto = (
    produto: ProdutoPedidoCompleto,
    grupos: GrupoComplemento[],
): SetProdutoAction => ({
    type: SET_PRODUTO,
    produto,
    grupos,
});

const setId = (id: number): SetIdAction => ({
    type: SET_ID,
    id,
});

const reset = () => ({ type: RESET });

export const alterarQuantidade = (quantidade: number): AlterarQuantidadeAction => ({
    type: ALTERAR_QUANTIDADE,
    quantidade,
});

export const alterarObservacao = (observacao: string): AlterarObservacaoAction => ({
    type: ALTERAR_OBSERVACAO,
    observacao,
});

const adicionarComplemento = (
    indice: number,
    quantidade: number,
    complementosId: number,
    grupo: GrupoComplemento,
): AdicionarComplementoAction => ({
    type: ADICIONAR_COMPLEMENTO,
    indice,
    quantidade,
    complementosId,
    grupo,
});

// Utils

const zerarQuantidade = (complemento: ComplementoItem) => ({ ...complemento, quantidade: 0 });

const zerarQuantidadeSeGrupoMaximoFor0 = (grupo: GrupoComplemento) => (
    grupo.maximo !== 1 ? i => i : zerarQuantidade
);

export const navegarParaProximoComplemento: KeyboardEvent => void = navegarProximoElemento('.m-widget1__item');
export const navegarParaComplementoAnterior: KeyboardEvent => void = navegarElementoAnterior('.m-widget1__item');

const converterComplementoEmComplementoItem = (
    complemento: Complemento,
    complementosGrupoId: number,
): ComplementoItem => ({
    ...complemento,
    complementosGrupoId,
    pedidosProdutosId: 0,
    quantidade: 0,
});

const irParaGrupoComplemento = (indice: number) => {
    const item = document.querySelector(`.focus-detalhes-${indice} [tabindex="0"]`);

    if (!item) {
        return;
    }

    scrollIntoView(item, {
        scrollMode: 'if-needed',
        behavior: 'smooth',
    });
};

const focarProximoItem = (indice: number) => {
    const item = document.querySelector(`.focus-detalhes-${indice} [tabindex="0"]`);
    if (item) {
        item.focus();
    }
};

export const getQuantidadeComplementos = (complementos: ComplementoItem[] = []) => (
    complementos
        .map(({ quantidade }) => quantidade)
        .reduce(sum, 0)
);


const isGrupoComplementoCompleto = ({ maximo, complementos }: GrupoComplementoSelecao) => (
    getQuantidadeComplementos(complementos) === maximo
);

const converterEmGrupoComplementoSelecao = (grupo: GrupoComplemento): GrupoComplementoSelecao => ({
    id: grupo.id,
    minimo: grupo.minimo,
    maximo: grupo.maximo,
    nome: grupo.nome,
    selecaoPreco: grupo.selecaoPreco,
    complementos: grupo
        .complementos
        .map(complemento => converterComplementoEmComplementoItem(complemento, grupo.id)),
});

const carregarQuantidadeComplementosSelecionados = (
    gruposComplemento: GrupoComplementoSelecao[],
    complementos: ComplementoItem[],
) => {
    complementos.forEach(({ id, quantidade }) => {
        const complemento = head(
            gruposComplemento
                .filter(grupo => !isGrupoComplementoCompleto(grupo))
                .map(grupo => grupo.complementos.find(c => c.id === id))
                .filter(Boolean),
        );

        if (complemento) {
            complemento.quantidade = quantidade;
        }
    });
};


const filtrarComplementosDoGrupo = (grupo: GrupoComplemento) => {
    const ids = grupo.complementos.map(({ id }) => id);
    return complemento => ids.includes(complemento.id);
};

const getComplementosSelecionadosDeFlattenComplementos = (
    complementos: ComplementoItem[],
    gruposDoProduto: GrupoComplemento[],
): ComplementoItem[][] => gruposDoProduto
    .map(grupo => complementos.filter(filtrarComplementosDoGrupo(grupo)));

// Selectors

export const getDetalhesProduto = (state: State) => state.balcao.detalhesProduto;

const getId = (state: State) => getDetalhesProduto(state).id;

export const getQuantidade = (state: State) => getDetalhesProduto(state).quantidade;

export const getObservacao = (state: State) => getDetalhesProduto(state).observacao;

export const isEdicao: State => boolean = (
    createSelector([getId, getItens], (id, itens) => !!getItemPorId(itens, id))
);

export const getProduto: State => ?ProdutoPedidoCompleto = createSelector(
    [getId, state => state, getItens],
    (id, state, itens) => {
        const item = getItemPorId(itens, id);

        if (item) {
            const produto = getProdutoPorId(state, item.produtosId);
            return converterProdutoParaItem(produto, item);
        }

        return converterProdutoParaItem(getProdutoPorId(state, id));
    },
);

export const getGruposComplementosId: State => number[] = createSelector(
    [getProduto],
    produto => (!produto ? [] : produto.gruposComplemento.map(grupo => grupo.id)),
);

export const getGruposComplementoDoProduto: State => GrupoComplemento[] = createSelector(
    [getGruposComplementosId, getGruposComplementos],
    (gruposComplementosIds, gruposComplemento) => (
        gruposComplementosIds
            .map(id => gruposComplemento.find(grupo => grupo.id === id))
            .filter(Boolean)
    ),
);

const getComplementosSelecionados = (state: State) => (
    getDetalhesProduto(state).complementosSelecionados
);

const getFlattenComplementosSelecionados = createSelector(
    getComplementosSelecionados,
    complementosSelecionados => flatten(complementosSelecionados),
);

export const getGruposComplementoSelecao: State => GrupoComplementoSelecao[] = createSelector(
    [getGruposComplementoDoProduto, getFlattenComplementosSelecionados],
    (grupos, complementosSelecionados) => {
        const gruposSelecao = grupos.map(converterEmGrupoComplementoSelecao);
        carregarQuantidadeComplementosSelecionados(gruposSelecao, complementosSelecionados);
        return gruposSelecao;
    },
);

export const isQuantidadeValida = (grupo: GrupoComplementoSelecao) => {
    const quantidade = getQuantidadeComplementos(grupo.complementos);
    return quantidade >= grupo.minimo && quantidade <= grupo.maximo;
};

export const isPermitidoAdicionar: State => boolean = createSelector(
    [getGruposComplementoSelecao],
    gruposComplemento => gruposComplemento.every(isQuantidadeValida),
);

export const getPrecoProduto: State => number = createSelector(
    [getProduto, getQuantidade, getFlattenComplementosSelecionados],
    (produto, quantidade, complementos) => {
        if (!produto) {
            return 0;
        }

        return precoTotalProduto({
            ...produto,
            quantidade,
            complementos,
        });
    },
);

const getComplementosPorComplementosSelecionados = (
    grupo: GrupoComplemento,
    complementos: ComplementoItem[],
): Complemento[] => {
    const ids = complementos.map(({ id }) => id);
    return grupo.complementos.filter(({ id }) => ids.includes(id));
};

const getMaiorPrecoComplemento = (complementos: Complemento[]): number => (
    complementos.reduce((maior, { preco }) => (
        preco > maior ? preco : maior
    ), 0)
);

// Thunks

export const buscarProdutoDetalhe = (id: number) => (
    dispatch: Function,
    getState: GetState,
) => {
    dispatch(buscarDados()).then(() => {
        dispatch(setId(id));

        const produto = getProduto(getState());
        const grupos = getGruposComplementoDoProduto(getState());
        if (!produto || !grupos) {
            dispatch(reset());
            return;
        }

        dispatch(setProduto(produto, grupos));
        focarProximoItem(0);
    });
};

export const adicionarItemDetalhes = () => (dispatch: Function, getState: GetState) => {
    const state = getState();
    const complementos = getFlattenComplementosSelecionados(state);
    const observacao = getObservacao(state);
    const quantidade = getQuantidade(state);
    const id = getId(state);

    if (isEdicao(state)) {
        dispatch(editarItem({
            id,
            quantidade,
            observacao,
            complementos,
        }));
    } else {
        dispatch(adicionarItem({
            produtosId: id,
            quantidade,
            observacao,
            complementos,
        }));
    }
    dispatch(reset());
    global.history.back();
};

export const adicionarComplementoScrollParaProximo = ({
    indice,
    quantidade,
    complementosId,
}: AdicionarComplemento) => (dispatch: Function, getState: GetState) => {
    const grupos = getGruposComplementoDoProduto(getState());
    const grupo = grupos[indice];
    dispatch(adicionarComplemento(
        indice,
        quantidade,
        complementosId,
        grupo,
    ));

    const gruposComplemento = getGruposComplementoSelecao(getState());
    const grupoComplemento = gruposComplemento[indice];
    if (!grupoComplemento) {
        return;
    }

    if (!isGrupoComplementoCompleto(grupoComplemento)) {
        return;
    }

    irParaGrupoComplemento(indice + 1);
    sleep(500).then(() => focarProximoItem(indice + 1));
};

const initialState = {
    id: 0,
    quantidade: 1,
    observacao: '',
    complementosSelecionados: [],
};

const detalhesProdutoReducer = (
    state: StateDetalhesProduto = initialState,
    action: Action,
): StateDetalhesProduto => {
    switch (action.type) {
    case SET_PRODUTO: {
        const {
            produto: {
                quantidade,
                observacao,
                complementos,
            },
            grupos,
        } = action;

        const complementosSelecionados = getComplementosSelecionadosDeFlattenComplementos(
            complementos,
            grupos,
        );

        return {
            ...state,
            quantidade: Math.max(1, quantidade),
            observacao,
            complementosSelecionados,
        };
    }
    case ALTERAR_QUANTIDADE:
        return {
            ...state,
            quantidade: Math.max(1, action.quantidade),
        };
    case ALTERAR_OBSERVACAO:
        return {
            ...state,
            observacao: action.observacao,
        };
    case ADICIONAR_COMPLEMENTO: {
        const {
            indice,
            complementosId,
            quantidade,
            grupo,
        } = action;
        if (quantidade < 0) {
            return state;
        }

        const complemento = grupo.complementos.find(({ id }) => id === complementosId);

        if (!complemento) {
            return state;
        }
        const complementoItem = converterComplementoEmComplementoItem(complemento, grupo.id);
        complementoItem.quantidade = quantidade;


        const outrosComplementos = (state.complementosSelecionados[indice] || [])
            .filter(({ id }) => id !== complementosId)
            .map(zerarQuantidadeSeGrupoMaximoFor0(grupo));

        let novosComplementos = [...outrosComplementos, complementoItem]
            .filter(c => c.quantidade > 0);

        const quantidadeSelecionada = getQuantidadeComplementos(novosComplementos);

        if (grupo.selecaoPreco === SELECAO_PRECO_MAIOR) {
            const maiorPreco = getMaiorPrecoComplemento(
                getComplementosPorComplementosSelecionados(grupo, novosComplementos),
            );

            const preco = maiorPreco / quantidadeSelecionada;
            novosComplementos = novosComplementos.map(c => ({ ...c, preco }));
        }

        if (quantidadeSelecionada > grupo.maximo) {
            return state;
        }

        const complementosSelecionados = [...state.complementosSelecionados];
        complementosSelecionados[indice] = novosComplementos;

        return {
            ...state,
            complementosSelecionados,
        };
    }
    case SET_ID:
        return {
            ...state,
            id: action.id,
        };
    case RESET:
    case LIMPAR_PEDIDO:
        return initialState;
    default:
        return state;
    }
};

export default detalhesProdutoReducer;
